import { useState } from "react";
import { motion } from "framer-motion";
import {
  AlertTriangle,
  Droplet,
  TestTube2,
  Ban,
  CalendarCog,
  LogOut,
  ListChecks,
  Smartphone,
  Download,
  Bell,
  LayoutGrid,
  Copy,
  Check,
} from "lucide-react";
import { SYMPTOMS, parseISO } from "@/lib/skincare";
import { api } from "@/lib/api";
import { canInstall, triggerInstall, isStandalone as getIsStandalone, isIOSSafari } from "@/lib/pwa";
import RoutineCustomizer from "@/components/RoutineCustomizer";
import { MORNING_ITEMS, NIGHT_ITEMS } from "@/lib/skincare";

const SAFETY = [
  {
    icon: AlertTriangle,
    title: "Never during pregnancy",
    body: "Isotretinoin can cause severe birth defects. Use contraception throughout treatment and 1 month after.",
  },
  {
    icon: TestTube2,
    title: "Blood tests matter",
    body: "Liver enzymes and lipids should be checked before & during treatment. Your prescription notes &ldquo;reports important&rdquo; — don't skip them.",
  },
  {
    icon: Ban,
    title: "Skip waxing, peels, laser",
    body: "Avoid waxing, dermabrasion, chemical peels, and laser during treatment and ~6 months after.",
  },
  {
    icon: Droplet,
    title: "Hydrate generously",
    body: "Dry lips, dry eyes and nosebleeds are common. Lip balm, eye drops and water = your best friends.",
  },
];

export default function HealthView({
  entry,
  setEntry,
  profile,
  setProfile,
  onLogout,
  isIOS,
  isStandalone,
}) {
  const [showStartEdit, setShowStartEdit] = useState(false);
  const [tempStart, setTempStart] = useState(profile?.start_date || "2026-06-16");
  const [showCustomizer, setShowCustomizer] = useState(false);
  const [showIOSInstall, setShowIOSInstall] = useState(false);
  const [showReminders, setShowReminders] = useState(false);
  const [morningTime, setMorningTime] = useState(profile?.morning_reminder_time || "10:00");
  const [nightTime, setNightTime] = useState(profile?.night_reminder_time || "22:00");
  const [widgetToken, setWidgetToken] = useState(null);
  const [showWidget, setShowWidget] = useState(false);
  const [copied, setCopied] = useState(false);
  const standalone = getIsStandalone();
  const ios = isIOSSafari();

  const loadWidgetToken = async () => {
    if (widgetToken) { setShowWidget((v) => !v); return; }
    try {
      const res = await api.getWidgetToken();
      setWidgetToken(res.token);
      setShowWidget(true);
    } catch {}
  };

  const copyWidgetUrl = () => {
    const url = `${window.location.origin}/widget/?t=${widgetToken}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const saveReminderTimes = async () => {
    try {
      const updated = await api.updateProfile({
        morning_reminder_time: morningTime,
        night_reminder_time: nightTime,
      });
      setProfile(updated);
      setShowReminders(false);
    } catch {}
  };

  const toggleSymptom = (id) => {
    const cur = entry?.symptoms || [];
    const next = cur.includes(id) ? cur.filter((s) => s !== id) : [...cur, id];
    setEntry({ ...entry, symptoms: next });
  };

  const saveStart = async () => {
    try {
      parseISO(tempStart); // validate
    } catch {
      return;
    }
    const updated = await api.updateProfile({ start_date: tempStart });
    setProfile(updated);
    setShowStartEdit(false);
  };

  return (
    <div className="px-6 pt-8 pb-10">
      <div className="text-xs font-bold uppercase tracking-[0.18em] text-[#6B7A70]">
        Health & Safety
      </div>
      <h2 className="font-heading text-3xl font-semibold text-[#2A332C] tracking-tight mt-1">
        Listen to your body.
      </h2>

      {/* Symptoms */}
      <div className="mt-6">
        <h3 className="font-heading text-base font-medium text-[#2A332C] mb-3">
          Today&rsquo;s side-effects
        </h3>
        <div className="flex flex-wrap gap-2" data-testid="symptom-tracker">
          {SYMPTOMS.map((s) => {
            const active = entry?.symptoms?.includes(s.id);
            return (
              <motion.button
                key={s.id}
                data-testid={`symptom-${s.id}`}
                whileTap={{ scale: 0.95 }}
                onClick={() => toggleSymptom(s.id)}
                className={`px-4 py-2 rounded-full border text-sm font-medium transition-colors ${
                  active
                    ? "bg-[#CC7A6B]/10 border-[#CC7A6B] text-[#CC7A6B]"
                    : "bg-white border-[#E9EFEA] text-[#6B7A70]"
                }`}
              >
                {s.label}
              </motion.button>
            );
          })}
        </div>
      </div>

      {/* Mood */}
      <div className="mt-6 p-4 bg-white rounded-3xl border border-[#E9EFEA]">
        <div className="text-sm font-medium text-[#2A332C] mb-2">How do you feel today?</div>
        <div className="flex justify-between" data-testid="mood-tracker">
          {["😞", "😐", "🙂", "😊", "🤩"].map((emoji, i) => {
            const v = i + 1;
            const active = entry?.mood === v;
            return (
              <button
                key={v}
                onClick={() => setEntry({ ...entry, mood: v })}
                className={`w-12 h-12 rounded-full text-2xl flex items-center justify-center transition-all ${
                  active ? "bg-[#E9F0E8] scale-110" : "bg-transparent"
                }`}
              >
                {emoji}
              </button>
            );
          })}
        </div>
      </div>

      {/* Safety cards */}
      <h3 className="font-heading text-base font-medium text-[#2A332C] mt-8 mb-3">
        Isotretinoin safety
      </h3>
      <div className="space-y-3">
        {SAFETY.map((s) => (
          <div
            key={s.title}
            className="p-4 bg-white rounded-2xl border border-[#E9EFEA] flex gap-3"
          >
            <div className="w-10 h-10 rounded-xl bg-[#FADBD8]/40 flex items-center justify-center shrink-0">
              <s.icon className="w-5 h-5 text-[#CC7A6B]" strokeWidth={1.5} />
            </div>
            <div>
              <div className="font-heading font-medium text-[#2A332C]">{s.title}</div>
              <div className="text-sm text-[#6B7A70] mt-0.5 leading-relaxed">{s.body}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Settings */}
      <h3 className="font-heading text-base font-medium text-[#2A332C] mt-8 mb-3">
        Settings
      </h3>
      <div className="space-y-3">
        {/* Reminder times */}
        <button
          onClick={() => setShowReminders((v) => !v)}
          className="w-full p-4 bg-white rounded-2xl border border-[#E9EFEA] flex items-center gap-3 text-left"
        >
          <div className="w-10 h-10 rounded-xl bg-[#E9F0E8] flex items-center justify-center shrink-0">
            <Bell className="w-5 h-5 text-[#4A6B53]" strokeWidth={1.5} />
          </div>
          <div className="flex-1">
            <div className="font-heading font-medium text-[#2A332C]">Reminder times</div>
            <div className="text-xs text-[#6B7A70]">
              Morning {profile?.morning_reminder_time || "10:00"} · Night {profile?.night_reminder_time || "22:00"}
            </div>
          </div>
        </button>

        {showReminders && (
          <div className="p-4 bg-white rounded-2xl border border-[#E9EFEA] space-y-4">
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-[#2A332C] w-24">🌅 Morning</span>
              <input
                type="time"
                value={morningTime}
                onChange={(e) => setMorningTime(e.target.value)}
                className="flex-1 px-3 py-2 rounded-xl border border-[#E9EFEA] text-sm text-[#2A332C] bg-[#F7F5F0]"
              />
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-[#2A332C] w-24">🌙 Night</span>
              <input
                type="time"
                value={nightTime}
                onChange={(e) => setNightTime(e.target.value)}
                className="flex-1 px-3 py-2 rounded-xl border border-[#E9EFEA] text-sm text-[#2A332C] bg-[#F7F5F0]"
              />
            </div>
            <button
              onClick={saveReminderTimes}
              className="w-full py-2.5 bg-[#2A332C] text-white rounded-full text-sm font-medium"
            >
              Save times
            </button>
          </div>
        )}

        <button
          data-testid="customize-routine-btn"
          onClick={() => setShowCustomizer(true)}
          className="w-full p-4 bg-white rounded-2xl border border-[#E9EFEA] flex items-center gap-3 text-left"
        >
          <div className="w-10 h-10 rounded-xl bg-[#E9F0E8] flex items-center justify-center shrink-0">
            <ListChecks className="w-5 h-5 text-[#4A6B53]" strokeWidth={1.5} />
          </div>
          <div className="flex-1">
            <div className="font-heading font-medium text-[#2A332C]">Customize routine</div>
            <div className="text-xs text-[#6B7A70]">Add, remove or rename steps</div>
          </div>
        </button>

        {/* Install app row — hidden once installed */}
        {!standalone && (
          <>
            <button
              onClick={async () => {
                if (ios) {
                  setShowIOSInstall((v) => !v);
                } else if (canInstall()) {
                  await triggerInstall();
                }
              }}
              className="w-full p-4 bg-white rounded-2xl border border-[#E9EFEA] flex items-center gap-3 text-left"
            >
              <div className="w-10 h-10 rounded-xl bg-[#E9F0E8] flex items-center justify-center shrink-0">
                {ios ? (
                  <Smartphone className="w-5 h-5 text-[#4A6B53]" strokeWidth={1.5} />
                ) : (
                  <Download className="w-5 h-5 text-[#4A6B53]" strokeWidth={1.5} />
                )}
              </div>
              <div className="flex-1">
                <div className="font-heading font-medium text-[#2A332C]">Add to Home Screen</div>
                <div className="text-xs text-[#6B7A70]">
                  {ios ? "Install as an iPhone app" : "Install as a native app"}
                </div>
              </div>
            </button>

            {showIOSInstall && (
              <div className="p-4 bg-[#F7F5F0] rounded-2xl border border-[#E9EFEA] space-y-2 text-sm text-[#2A332C]">
                <div className="flex items-center gap-2">
                  <span className="font-bold">1.</span>
                  <span>Tap <span className="font-semibold">Share</span> in Safari&rsquo;s toolbar</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-bold">2.</span>
                  <span>Tap <span className="font-semibold">Add to Home Screen</span></span>
                </div>
                <div className="text-xs text-[#6B7A70]">Then open from your home screen for push reminders.</div>
              </div>
            )}
          </>
        )}

        {/* iOS Widget */}
        <button
          onClick={loadWidgetToken}
          className="w-full p-4 bg-white rounded-2xl border border-[#E9EFEA] flex items-center gap-3 text-left"
        >
          <div className="w-10 h-10 rounded-xl bg-[#E9F0E8] flex items-center justify-center shrink-0">
            <LayoutGrid className="w-5 h-5 text-[#4A6B53]" strokeWidth={1.5} />
          </div>
          <div className="flex-1">
            <div className="font-heading font-medium text-[#2A332C]">iOS Widget</div>
            <div className="text-xs text-[#6B7A70]">Add to your home screen via WidgetWeb app</div>
          </div>
        </button>

        {showWidget && widgetToken && (
          <div className="p-4 bg-white rounded-2xl border border-[#E9EFEA] space-y-3">
            <p className="text-xs text-[#6B7A70] leading-relaxed">
              1. Install <span className="font-semibold text-[#2A332C]">WidgetWeb</span> from the App Store<br/>
              2. Copy the URL below and paste it in WidgetWeb<br/>
              3. Add the widget to your home screen
            </p>
            <div className="flex items-center gap-2 p-2.5 bg-[#F7F5F0] rounded-xl">
              <span className="flex-1 text-[11px] text-[#2A332C] break-all font-mono">
                {window.location.origin}/widget/?t={widgetToken.slice(0, 12)}…
              </span>
              <button
                onClick={copyWidgetUrl}
                className="shrink-0 w-8 h-8 rounded-lg bg-[#2A332C] text-white flex items-center justify-center"
              >
                {copied
                  ? <Check className="w-3.5 h-3.5" strokeWidth={2.5} />
                  : <Copy className="w-3.5 h-3.5" strokeWidth={2} />}
              </button>
            </div>
          </div>
        )}

        <button
          data-testid="edit-start-date-btn"
          onClick={() => setShowStartEdit(!showStartEdit)}
          className="w-full p-4 bg-white rounded-2xl border border-[#E9EFEA] flex items-center gap-3 text-left"
        >
          <div className="w-10 h-10 rounded-xl bg-[#E9F0E8] flex items-center justify-center shrink-0">
            <CalendarCog className="w-5 h-5 text-[#4A6B53]" strokeWidth={1.5} />
          </div>
          <div className="flex-1">
            <div className="font-heading font-medium text-[#2A332C]">Start date</div>
            <div className="text-xs text-[#6B7A70]">{profile?.start_date}</div>
          </div>
        </button>

        {showStartEdit && (
          <div className="p-4 bg-white rounded-2xl border border-[#E9EFEA] flex gap-2">
            <input
              type="date"
              value={tempStart}
              onChange={(e) => setTempStart(e.target.value)}
              className="flex-1 px-3 py-2 rounded-xl border border-[#E9EFEA] text-sm"
              data-testid="start-date-input"
            />
            <button
              onClick={saveStart}
              data-testid="save-start-date-btn"
              className="px-4 py-2 bg-[#2A332C] text-white rounded-xl text-sm font-medium"
            >
              Save
            </button>
          </div>
        )}

        <button
          data-testid="logout-btn"
          onClick={onLogout}
          className="w-full p-4 bg-white rounded-2xl border border-[#E9EFEA] flex items-center gap-3 text-left"
        >
          <div className="w-10 h-10 rounded-xl bg-[#FADBD8]/40 flex items-center justify-center shrink-0">
            <LogOut className="w-5 h-5 text-[#CC7A6B]" strokeWidth={1.5} />
          </div>
          <div className="font-heading font-medium text-[#2A332C]">Sign out</div>
        </button>
      </div>

      {showCustomizer && (
        <RoutineCustomizer
          profile={profile}
          setProfile={setProfile}
          defaultMorning={MORNING_ITEMS}
          defaultNight={NIGHT_ITEMS}
          onClose={() => setShowCustomizer(false)}
        />
      )}
    </div>
  );
}
